import { useEffect, useState } from "react";
import { Button, FormLabel } from "@components";
import { cn } from "@utils";
import { createQuery, uploadQueryAttachment, fetchQueryReasons, useUsers } from "@api";
import { getServerErrorMessage } from "@api";
import { useUser } from "@auth";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircleIcon } from "@heroicons/react/24/solid";
import { ExclamationCircleIcon, PaperClipIcon, XMarkIcon } from "@heroicons/react/24/outline";
import SearchSelect from "../../queries/components/SearchSelect";

const DETAILS_MAX = 500;

const ReasonGrid = ({ heading = "Reason for query", options, value, onChange, error }) => (
  <div>
    <FormLabel>{heading}</FormLabel>
    <div role="radiogroup" aria-label={heading} className="grid grid-cols-2 sm:grid-cols-3 gap-2">
      {options.map(({ code, label }) => {
        const selected = value === code;
        return (
          <button
            key={code}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(code)}
            className={cn(
              "flex items-center justify-center min-h-16 rounded-xl border px-3 py-2.5 text-sm font-medium text-center transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1",
              selected
                ? "border-primary-500 bg-primary-50 text-primary-700 shadow-sm"
                : "border-zinc-200 bg-white text-zinc-700 hover:border-zinc-300 hover:bg-zinc-50",
            )}
          >
            {label}
          </button>
        );
      })}
    </div>
    {error && (
      <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
        <ExclamationCircleIcon className="size-4 shrink-0" />
        {error}
      </p>
    )}
  </div>
);

const DetailsTextarea = ({ value, onChange, placeholder, maxLength = DETAILS_MAX }) => (
  <div className="relative rounded-xl border border-zinc-200 overflow-hidden transition-all focus-within:ring-2 focus-within:ring-offset-0 focus-within:ring-primary-500 focus-within:border-primary-500">
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      rows={4}
      maxLength={maxLength}
      placeholder={placeholder}
      className="block w-full px-3 pt-2.5 pb-7 text-sm focus:outline-none resize-none bg-white text-zinc-900 placeholder:text-zinc-400"
    />
    <div className="absolute bottom-1.5 right-3 pointer-events-none">
      <span
        className={cn(
          "text-xs tabular-nums",
          value.length > maxLength - 10 ? "text-red-500" : value.length > maxLength - 50 ? "text-amber-500" : "text-zinc-400",
        )}
      >
        {value.length} / {maxLength}
      </span>
    </div>
  </div>
);

// The body of the staff-raised query form: reason selection (invoice- or line-item-level),
// optional details, submit, and success state. Rendered inside a Modal by callers so the same
// form serves both the trade-account drawer and the "New query" flow on the Queries page.
// `invoices` may hold several invoices, but line-item mode (only reached from the drawer) always
// carries exactly one.
const RaiseQueryForm = ({ invoices = [], lineItems, onCancel, cancelLabel = "Cancel", onDone, collectAssignee = false }) => {
  const isLineItemMode = lineItems?.length > 0;
  const queryClient = useQueryClient();
  const { user } = useUser();
  const { data: users } = useUsers();

  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const [lineReasons, setLineReasons] = useState({});
  const [assignee, setAssignee] = useState(null);
  const [priority, setPriority] = useState(null);
  const [files, setFiles] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const invoiceIdsKey = invoices.map((i) => i.id).join(",");

  // Default the assignee to yourself in the New-query flow.
  useEffect(() => {
    if (!collectAssignee) return;
    const me = (users ?? []).find((u) => u.id === user?.guid);
    setAssignee(me ?? (user ? { id: user.guid, name: user.name, email: user.email } : null));
  }, [collectAssignee, users, user]);

  useEffect(() => {
    setReason("");
    setDetails("");
    setLineReasons({});
    setPriority(null);
    setFiles([]);
    setSubmitted(false);
    setShowSuccess(false);
  }, [invoiceIdsKey]);

  const { data: reasonsData } = useQuery({
    queryKey: ["queries", "reasons"],
    queryFn: ({ signal }) => fetchQueryReasons({ signal }),
    staleTime: 60 * 60 * 1000,
  });
  const reasons = reasonsData ?? [];

  const { mutate, isPending, error: mutationError } = useMutation({
    mutationFn: async () => {
      if (isLineItemMode) {
        return createQuery({
          ledgerTransactionIds: invoices.map((i) => i.id),
          lineItems: lineItems.map((li) => ({ lineItemId: li.id, reason: lineReasons[li.id] })),
        });
      }

      const attachments =
        collectAssignee && files.length > 0 ? await Promise.all(files.map(uploadQueryAttachment)) : undefined;

      return createQuery({
        ledgerTransactionIds: invoices.map((i) => i.id),
        reason,
        details: details || undefined,
        assignedToUserId: collectAssignee ? assignee?.id : undefined,
        assignedToName: collectAssignee ? assignee?.name : undefined,
        priority: collectAssignee ? priority : undefined,
        attachments,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["queries"] });
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        onDone?.();
      }, 1500);
    },
  });

  const isValid = isLineItemMode
    ? lineItems.every((li) => lineReasons[li.id])
    : !!reason && (!collectAssignee || priority != null);

  const handleSubmit = () => {
    setSubmitted(true);
    if (isValid) mutate();
  };

  const errorMessage =
    getServerErrorMessage(mutationError) ||
    mutationError?.response?.data?.title ||
    (mutationError ? "Something went wrong. Please try again." : null);

  if (showSuccess) {
    return (
      <div className="p-5 sm:p-6 bg-white">
        <div className="flex flex-col items-center justify-center gap-4 py-10">
          <div className="relative flex items-center justify-center">
            <div className="size-20 rounded-full bg-primary-50" />
            <CheckCircleIcon className="size-10 text-primary-500 absolute" />
          </div>
          <div className="text-center space-y-1">
            <p className="text-base font-semibold text-zinc-900">Query raised</p>
            <p className="text-sm text-zinc-500">
              {invoices.length > 1
                ? "These invoices have been marked as under query."
                : "This invoice has been marked as under query."}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-5 sm:p-6 bg-white">
      <div className="flex flex-col gap-5">
        {isLineItemMode ? (
          <div className="flex flex-col divide-y divide-zinc-100 -my-1">
            {lineItems.map((li) => (
              <div key={li.id} className="py-3.5 first:pt-0 last:pb-0 flex flex-col gap-2">
                <p className="text-sm font-medium text-zinc-800 leading-snug">{li.description ?? "Line item"}</p>
                <ReasonGrid
                  heading=""
                  options={reasons}
                  value={lineReasons[li.id] ?? ""}
                  onChange={(code) => setLineReasons((prev) => ({ ...prev, [li.id]: code }))}
                  error={submitted && !lineReasons[li.id] ? "Please select a reason" : null}
                />
              </div>
            ))}
          </div>
        ) : (
          <>
            <SearchSelect
              label="Reason"
              placeholder="Select a reason…"
              value={reasons.find((r) => r.code === reason) ?? null}
              onSelect={(r) => setReason(r?.code ?? "")}
              displayValue={(r) => r.label}
              getKey={(r) => r.code}
              sortBy={(r) => r.label}
              renderOption={(r) => <span className="text-sm text-zinc-900">{r.label}</span>}
              options={reasons}
              filterOption={(r, term) => r.label.toLowerCase().includes(term.toLowerCase())}
              emptyText="No reasons"
              error={submitted && !reason ? "Please select a reason" : null}
            />
            <div className="border-t border-zinc-100" />
            <div>
              <FormLabel>
                Details <span className="text-zinc-400 font-normal">(optional)</span>
              </FormLabel>
              <DetailsTextarea value={details} onChange={setDetails} placeholder="Describe the issue with this invoice..." />
            </div>
            {collectAssignee && (
              <>
                <div className="border-t border-zinc-100" />
                <SearchSelect
                  label="Priority"
                  placeholder="Select priority…"
                  value={priority}
                  onSelect={(n) => setPriority(n ?? null)}
                  displayValue={(n) => String(n)}
                  getKey={(n) => n}
                  sortBy={(n) => n}
                  renderOption={(n) => <span className="text-sm text-zinc-900">{n}</span>}
                  options={Array.from({ length: 10 }, (_, i) => i + 1)}
                  filterOption={(n, term) => String(n).includes(term)}
                  emptyText="No priorities"
                  error={submitted && priority == null ? "Please select a priority" : null}
                />
                <SearchSelect
                  label="Assign to"
                  placeholder="Search team…"
                  value={assignee}
                  onSelect={setAssignee}
                  displayValue={(u) => u.name}
                  getKey={(u) => u.id}
                  sortBy={(u) => u.name ?? ""}
                  renderOption={(u) => (
                    <>
                      <span className="text-sm text-zinc-900">{u.name}</span>
                      {u.email && <span className="text-xs text-zinc-500">{u.email}</span>}
                    </>
                  )}
                  options={users ?? []}
                  filterOption={(u, term) => (u.name ?? "").toLowerCase().includes(term.toLowerCase())}
                  emptyText="No users found"
                />
                <div>
                  <FormLabel>
                    Attachments <span className="text-zinc-400 font-normal">(optional)</span>
                  </FormLabel>
                  <label className="flex items-center gap-2 cursor-pointer rounded-xl border border-dashed border-zinc-300 px-3.5 py-2.5 text-sm text-zinc-600 hover:border-zinc-400 hover:bg-zinc-50">
                    <PaperClipIcon className="size-4 shrink-0" />
                    <span>Add files</span>
                    <input
                      type="file"
                      multiple
                      className="hidden"
                      onChange={(e) => {
                        setFiles((prev) => [...prev, ...Array.from(e.target.files ?? [])]);
                        e.target.value = "";
                      }}
                    />
                  </label>
                  {files.length > 0 && (
                    <ul className="flex flex-col gap-1.5 mt-2">
                      {files.map((f, i) => (
                        <li
                          key={`${f.name}-${i}`}
                          className="flex items-center justify-between gap-2 rounded-lg border border-zinc-100 bg-zinc-50 px-3 py-2 text-sm"
                        >
                          <span className="truncate text-zinc-700">{f.name}</span>
                          <button
                            type="button"
                            aria-label={`Remove ${f.name}`}
                            onClick={() => setFiles((prev) => prev.filter((_, idx) => idx !== i))}
                            className="shrink-0 rounded-md p-0.5 text-zinc-400 hover:bg-zinc-200 hover:text-zinc-600"
                          >
                            <XMarkIcon className="size-4" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </>
            )}
          </>
        )}

        {errorMessage && !isPending && (
          <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5">
            <ExclamationCircleIcon className="size-4 text-red-500 shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{errorMessage}</p>
          </div>
        )}

        <div className="flex flex-col-reverse sm:flex-row gap-3 pt-1">
          <Button color="secondary" className="sm:flex-1" onClick={onCancel} disabled={isPending}>
            {cancelLabel}
          </Button>
          <Button
            color="primary"
            className="sm:flex-1"
            onClick={handleSubmit}
            disabled={isPending}
            submitting={isPending}
          >
            Raise query
          </Button>
        </div>
      </div>
    </div>
  );
};

export default RaiseQueryForm;
